import http from '../../../../api/http-common.jsx';
import { debugLog } from './debugLog';

class FormsService {
  constructor() {
    this.questions = [];
    this.infoFicha = null;
    this.infoGeneral = null;
    this.snapshot = null;
  }

  /**
   * Obtiene la configuración del formulario cargada por loadFormByUuid
   * @returns {Array}
   */
  getConfigForm() {
    return this.questions;
  }

  /**
   * Obtiene la información general
   * @returns {Object|null}
   */
  getInfoGeneral() {
    return this.infoGeneral;
  }

  /**
   * Versión de la ficha con que se armó el formulario
   * ({ kobo_version_id, version_solicitada, origen: 'snapshot' | 'vigente_sin_snapshot' })
   * @returns {Object|null}
   */
  getSnapshot() {
    return this.snapshot;
  }

  /**
   * Establece información de debug general
   */
  setDebugInfoGeneral(debugInfo) {
    debugLog('🔧 Configurando información de debug:', debugInfo);
    this.debugInfoGeneral = debugInfo;
  }

  /**
   * Cargar formulario específico por UUID de ficha
   * @param {string} fichaUuid - UUID de la ficha
   * @returns {Promise<Object>} Datos del formulario específico
   */
  async loadFormByUuid(fichaUuid) {
    try {
      debugLog('🔄 Cargando formulario para ficha UUID:', fichaUuid);

      debugLog('📡 Haciendo petición a API:', `/ficha/formulario/master/configuracion?uuid=${fichaUuid}`);
      const response = await http.get(
        `/ficha/formulario/master/configuracion?uuid=${fichaUuid}`
      );
      debugLog('📥 Respuesta de API recibida:', response.status, response.data);
      const data = response.data.data;

      // Limpiar datos existentes
      this.questions = [];
      this.infoFicha = null;
      this.infoGeneral = null;
      this.snapshot = null;

      // Asignar los datos según la nueva estructura
      if (data.configForm) {
        this.questions = data.configForm;
        debugLog('✅ ConfigForm cargado:', this.questions.length, 'preguntas');
      }
      if (data.infoFicha) {
        this.infoFicha = data.infoFicha;
        debugLog('✅ InfoFicha cargada para UUID:', fichaUuid, this.infoFicha);
      }
      if (data.infoGeneral) {
        this.infoGeneral = data.infoGeneral;
        debugLog('✅ InfoGeneral cargada:', this.infoGeneral);
      }
      if (data.snapshot) {
        this.snapshot = data.snapshot;
        debugLog('✅ Versión de la ficha:', this.snapshot);
      }

      debugLog('🎉 Formulario específico cargado exitosamente:', {
        uuid: fichaUuid,
        preguntas: this.questions.length,
        infoFicha: !!this.infoFicha,
        infoGeneral: !!this.infoGeneral,
      });

      return {
        questions: this.questions,
        infoFicha: this.infoFicha,
        infoGeneral: this.infoGeneral,
        snapshot: this.snapshot,
      };
    } catch (error) {
      console.error('❌ Error al cargar formulario por UUID:', fichaUuid, error);
      throw new Error(`No se pudo cargar el formulario para la ficha: ${error.message}`);
    }
  }

  /**
   * Obtiene la miniatura de una imagen desde el sincronizador ODK
   * @param {string} filename - Nombre del archivo de imagen
   * @returns {Promise<string>} URL de la miniatura de la imagen
   */
  async getImageThumbnail(filename) {
    if (!filename) {
      throw new Error('Nombre de archivo es requerido para obtener la miniatura');
    }

    try {
      const response = await http.get(
        `${filename}`,
        { responseType: 'blob' }
      );
      
      // Crear URL del blob para mostrar la imagen
      const imageUrl = URL.createObjectURL(response.data);
      
      // debugLog('Miniatura de imagen obtenida:', {
      //   filename,
      //   imageUrl
      // });
      
      return imageUrl;
    } catch (error) {
      // console.error('Error al obtener miniatura de imagen:', error);
      throw new Error(`No se pudo obtener la miniatura de la imagen: ${error.message}`);
    }
  }

  /**
   * Guarda los valores modificados de un grupo de formulario
   * @param {Object} formValues - Valores del formulario del grupo (incluye archivos si los hay)
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async saveGroupData(formValues) {
    if (!formValues || typeof formValues !== 'object') {
      throw new Error('Los valores del formulario son requeridos');
    }

    try {
      const { files, ...dataToSend } = formValues;
      
      // Si hay archivos, usar FormData para envío multipart
      if (files && Object.keys(files).length > 0) {
        const formData = new FormData();
        
        // Agregar los datos JSON como string
        formData.append('data', JSON.stringify(dataToSend));
        
        // Agregar cada archivo
        Object.entries(files).forEach(([fieldName, file]) => {
          formData.append(`files[${fieldName}]`, file, file.name);
        });
        
        // debugLog('Enviando datos con archivos:', {
        //   dataFields: Object.keys(dataToSend),
        //   fileFields: Object.keys(files),
        //   fileNames: Object.values(files).map(f => f.name)
        // });
        
        const response = await http.post(
          '/ficha/formulario/master/guarda',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        // debugLog('Datos del grupo con archivos guardados exitosamente:', response.data);
        return response.data;
      } else {
        // Si no hay archivos, enviar como JSON normal
        // debugLog('Enviando datos sin archivos:', dataToSend);
        
        const response = await http.post(
          '/ficha/formulario/master/guarda',
          dataToSend
        );
        
        // debugLog('Datos del grupo guardados exitosamente:', response.data);
        return response.data;
      }
    } catch (error) {
      // console.error('Error al guardar datos del grupo:', error);
      const status = error?.response?.status;
      const serverData = error?.response?.data;
      const message = `No se pudieron guardar los datos${status ? `: ${status}` : ''}`;
      const err = new Error(message);
      err.status = status;
      err.server = serverData;
      err.serverMessage = serverData?.message;
      err.responseData = serverData?.data;
      throw err;
    }
  }


}

// Crear una instancia única del servicio
const formsService = new FormsService();

export default formsService;
